import apiClient from "@/api/client";
import type { AdvisoryResponse, AdvisorySuggestion } from "./types";

const BASE = "/power-projects";
const MULTIPART = { headers: { "Content-Type": "multipart/form-data" } } as const;

async function unwrap<T>(p: Promise<{ data: { data: T } }>): Promise<T> {
  const r = await p;
  return r.data.data;
}

export const getAdvisory = (connectionId: string) =>
  unwrap<AdvisoryResponse>(apiClient.get(`${BASE}/connections/${connectionId}/advisory/`));

export const recomputeAdvisory = (connectionId: string) =>
  unwrap<{ task_id: string }>(
    apiClient.post(`${BASE}/connections/${connectionId}/advisory/recompute/`),
  );

export const markAdvisorySeen = (connectionId: string) =>
  unwrap<{ seen: boolean }>(
    apiClient.post(`${BASE}/connections/${connectionId}/advisory/seen/`),
  );

export const getAdvisorySnapshot = (connectionId: string) =>
  unwrap<Record<string, unknown>>(
    apiClient.get(`${BASE}/connections/${connectionId}/advisory-snapshot/`),
  );

export const createAdvisorNote = (connectionId: string, description: string, files: File[]) => {
  const fd = new FormData();
  fd.append("description", description);
  files.forEach((f) => fd.append("files", f));
  return unwrap<{ id: string }>(
    apiClient.post(`${BASE}/connections/${connectionId}/advisor-notes/`, fd, MULTIPART),
  );
};

export const putDirectives = (connectionId: string, text: string) =>
  unwrap<{ text: string }>(
    apiClient.put(`${BASE}/connections/${connectionId}/directives/`, { text }),
  );

export const suggestionFeedback = (id: string, status: "done" | "dismissed") =>
  unwrap<AdvisorySuggestion>(
    apiClient.patch(`${BASE}/advisory-suggestions/${id}/feedback/`, { status }),
  );

export const suggestionComment = (id: string, body: string) =>
  unwrap<AdvisorySuggestion>(
    apiClient.post(`${BASE}/advisory-suggestions/${id}/comment/`, { body }),
  );

export const getOversight = () =>
  unwrap<import("./types").OversightRow[]>(apiClient.get(`${BASE}/advisory-oversight/`));

export const getAnalytics = (params: Record<string, string | string[]>) =>
  unwrap<import("./types").Analytics>(apiClient.get(`${BASE}/advisory-analytics/`, { params }));

export const applySuggestion = (id: string) =>
  unwrap<AdvisorySuggestion>(apiClient.post(`${BASE}/advisory-suggestions/${id}/apply/`));

export const getProjectCompare = (ids: string[]) =>
  unwrap<{ projects: import("./types").ProjectCompareCol[] }>(
    apiClient.get(`${BASE}/advisory-compare/`, { params: { ids: ids.join(",") } }),
  );

export const getCoordinators = (params: Record<string, string | string[]>) =>
  unwrap<import("./types").CoordinatorsData>(apiClient.get(`${BASE}/advisory-coordinators/`, { params }));

export const getMeta = (params: Record<string, string>) =>
  unwrap<import("./types").MetaData>(apiClient.get(`${BASE}/advisory-meta/`, { params }));

export const promoteSuggestion = (id: string) =>
  unwrap<{ knowledge_entry_id: string; status: string }>(
    apiClient.post(`${BASE}/advisory-suggestions/${id}/promote/`),
  );

export const getControlBoard = (params: Record<string, string> = {}) =>
  unwrap<import("./types").ControlBoardRow[]>(apiClient.get(`${BASE}/advisory-control-board/`, { params }));

export const listSlaRules = (params: Record<string, string> = {}) =>
  apiClient.get<{ data: import("./types").SlaRule[] } | import("./types").SlaRule[]>(`${BASE}/sla-rules/`, { params })
    .then((r) => (Array.isArray(r.data) ? r.data : (r.data as { data: import("./types").SlaRule[] }).data ?? r.data));

export const createSlaRule = (body: Partial<import("./types").SlaRule>) =>
  apiClient.post(`${BASE}/sla-rules/`, body).then((r) => r.data);

export const deleteSlaRule = (id: string) => apiClient.delete(`${BASE}/sla-rules/${id}/`);

export const patchSlaRule = (id: string, body: Partial<import("./types").SlaRule>) =>
  apiClient.patch(`${BASE}/sla-rules/${id}/`, body).then((r) => r.data);

// ── zegary lokalizacji (per kategoria właściciela działki) ──────────────────────
export const getLocationClockConfig = () =>
  unwrap<import("./types").LocationClockConfigRow[]>(
    apiClient.get(`${BASE}/sla-rules/location-clock-config/`));

export const getLocationTrackTemplates = () =>
  unwrap<import("./types").LocationTrackTemplateRow[]>(
    apiClient.get(`${BASE}/sla-rules/location-track-templates/`));

export const putLocationTrackTemplate = (category: string, element_kinds: string[]) =>
  unwrap<import("./types").LocationTrackTemplateRow[]>(
    apiClient.put(`${BASE}/sla-rules/location-track-templates/`, { category, element_kinds }));

export const resetLocationTracks = (category: string) =>
  unwrap<{ category: string; parcels_reset: number }>(
    apiClient.post(`${BASE}/sla-rules/location-track-templates/reset/`, { category }));

// ── tor lokalizacji działki (bloczki) ───────────────────────────────────────────
export const getParcelTrack = (parcelId: string) =>
  unwrap<import("./types").ParcelTrack>(apiClient.get(`${BASE}/parcels/${parcelId}/track/`));

export const addParcelTrackElement = (parcelId: string, body: Record<string, unknown>) =>
  unwrap<import("./types").ParcelTrack>(apiClient.post(`${BASE}/parcels/${parcelId}/track/`, body));

export const patchParcelTrackElement = (parcelId: string, elementId: string, body: Record<string, unknown>) =>
  unwrap<import("./types").ParcelTrack>(
    apiClient.patch(`${BASE}/parcels/${parcelId}/track/${elementId}/`, body));

export const deleteParcelTrackElement = (parcelId: string, elementId: string) =>
  unwrap<import("./types").ParcelTrack>(
    apiClient.delete(`${BASE}/parcels/${parcelId}/track/${elementId}/`));

export const resetParcelTrack = (parcelId: string) =>
  unwrap<import("./types").ParcelTrack>(apiClient.post(`${BASE}/parcels/${parcelId}/reset-track/`));

// ── rykoszet przeprojektowania ──────────────────────────────────────────────────
export const getRouteImpact = (connectionId: string) =>
  unwrap<{ parcels: import("./types").RouteImpactParcel[] }>(
    apiClient.get(`${BASE}/connections/${connectionId}/route-impact/`));

export const applyRouteImpact = (connectionId: string, revert: string[], keep: string[]) =>
  unwrap<{ reverted: number; kept: number }>(
    apiClient.post(`${BASE}/connections/${connectionId}/route-impact/apply/`,
      { revert_parcel_ids: revert, keep_parcel_ids: keep }));

export const getControl = (connectionId: string) =>
  unwrap<import("./types").ControlState>(apiClient.get(`${BASE}/connections/${connectionId}/control/`));

export const getProcessGraph = (connectionId: string, expandLocation = false) =>
  unwrap<import("./types").ProcessGraph>(
    apiClient.get(`${BASE}/connections/${connectionId}/process-graph/`,
      expandLocation ? { params: { expand: "location" } } : undefined),
  );

export const getParcelGraph = (connectionId: string) =>
  unwrap<import("./types").ParcelGraph>(
    apiClient.get(`${BASE}/connections/${connectionId}/process-graph/`, { params: { view: "parcels" } }),
  );

export const addGraphEdge = (connectionId: string, node: string, depends_on: string) =>
  unwrap<import("./types").ProcessGraph>(
    apiClient.post(`${BASE}/connections/${connectionId}/process-graph/edge/`, { node, depends_on }),
  );

export const removeGraphEdge = (connectionId: string, node: string, depends_on: string) =>
  unwrap<import("./types").ProcessGraph>(
    apiClient.delete(`${BASE}/connections/${connectionId}/process-graph/edge/`, { data: { node, depends_on } }),
  );

export const setGraphNodeState = (connectionId: string, node: string, state: string, reason: string) =>
  unwrap<import("./types").ProcessGraph>(
    apiClient.post(`${BASE}/connections/${connectionId}/process-graph/node-state/`, { node, state, reason }),
  );

// Ręczna pozycja kafelka na planszy (kosmetyka) — nie rusza zależności DAG.
export const setGraphNodePosition = (connectionId: string, node: string, x: number, y: number) =>
  unwrap<{ node: string; x: number; y: number }>(
    apiClient.post(`${BASE}/connections/${connectionId}/process-graph/node-position/`, { node, x, y }),
  );

export const clearGraphPositions = (connectionId: string) =>
  unwrap<{ cleared: boolean }>(
    apiClient.delete(`${BASE}/connections/${connectionId}/process-graph/node-position/`),
  );

export const setGraphNodePhases = (
  connectionId: string, node: string,
  fields: { submit_label?: string; resolve_label?: string; submit_sla_days?: number | null; resolve_sla_days?: number | null },
) =>
  unwrap<import("./types").ProcessGraph>(
    apiClient.post(`${BASE}/connections/${connectionId}/process-graph/node-phases/`, { node, ...fields }),
  );

export const getGraphProposals = (connectionId: string) =>
  unwrap<{ proposals: import("./types").FactProposal[]; track_proposals: import("./types").TrackProposal[] }>(
    apiClient.get(`${BASE}/connections/${connectionId}/process-graph/proposals/`),
  );

export const applyTrackProposal = (
  connectionId: string,
  p: { parcel_id: string; element_id: string | null; element_kind: string; action: string },
) =>
  unwrap<{ applied: import("./types").TrackProposal }>(
    apiClient.post(`${BASE}/connections/${connectionId}/process-graph/track-proposals/apply/`, p),
  );

export const getTemplateCalibration = (templateId: string) =>
  unwrap<import("./types").PhaseCalibration>(
    apiClient.get(`${BASE}/process-templates/${templateId}/calibration/`),
  );

export const getProjectTodo = (connectionId: string) =>
  unwrap<{ items: import("./types").TodoItem[] }>(
    apiClient.get(`${BASE}/connections/${connectionId}/todo/`),
  );

export const getGlobalTodoFilters = () =>
  unwrap<import("./types").GlobalTodoFilters>(
    apiClient.get(`${BASE}/advisory-todo/`, { params: { filters: 1 } }),
  );

export const getGlobalTodo = (filters?: { projects?: string[]; designers?: string[] }) => {
  // Powtórzone parametry (?project=a&project=b) — DRF czyta je przez getlist.
  const params = new URLSearchParams();
  filters?.projects?.forEach((id) => params.append("project", id));
  filters?.designers?.forEach((id) => params.append("designer", id));
  return unwrap<{ items: import("./types").TodoItem[]; scope: "all" | "self" }>(
    apiClient.get(`${BASE}/advisory-todo/`, { params }),
  );
};

// ── własne zadania „Moje TODO" (poza projektami, tylko własne) ─────────────────
export const listMyTasks = () =>
  unwrap<import("./types").PersonalTask[]>(apiClient.get(`${BASE}/my-tasks/`));

export const createMyTask = (body: import("./types").PersonalTaskInput) =>
  unwrap<import("./types").PersonalTask>(apiClient.post(`${BASE}/my-tasks/`, body));

export const patchMyTask = (id: string, body: Partial<import("./types").PersonalTaskInput>) =>
  unwrap<import("./types").PersonalTask>(apiClient.patch(`${BASE}/my-tasks/${id}/`, body));

export const deleteMyTask = (id: string) => apiClient.delete(`${BASE}/my-tasks/${id}/`);

export const resetProcessGraph = (connectionId: string, templateId?: string) =>
  unwrap<import("./types").ProcessGraph>(
    apiClient.post(`${BASE}/connections/${connectionId}/process-graph/reset/`,
      templateId ? { template: templateId } : {}),
  );

// ── metaszablony procesu (per klient + domyślny) ──
export const listProcessTemplates = async (): Promise<import("./types").ProcessTemplate[]> => {
  const d = await unwrap<{ results?: import("./types").ProcessTemplate[] } | import("./types").ProcessTemplate[]>(
    apiClient.get(`${BASE}/process-templates/`, { params: { page_size: 200 } }),
  );
  return Array.isArray(d) ? d : d.results ?? [];
};

export const saveProcessTemplate = (t: import("./types").ProcessTemplate) =>
  unwrap<import("./types").ProcessTemplate>(
    t.id
      ? apiClient.put(`${BASE}/process-templates/${t.id}/`, t)
      : apiClient.post(`${BASE}/process-templates/`, t),
  );

export const deleteProcessTemplate = (id: string) =>
  apiClient.delete(`${BASE}/process-templates/${id}/`);

export const listTemplateTargets = (templateId?: string) =>
  unwrap<{ targets: import("./types").TemplateTarget[] }>(
    apiClient.get(`${BASE}/process-templates/targets/`,
      { params: templateId ? { template: templateId } : {} }),
  );

// reset=true → hurtowy odpowiednik „Resetuj do szablonu" (akcje niebezpieczne:
// kasuje kroki spoza szablonu, ręczne zależności i układ kafelków). Backend wpuszcza
// tu wyłącznie dyrektora technicznego / CEO.
export const applyProcessTemplate = (id: string, connections: string[], reset = false) =>
  unwrap<{ applied: number; reset: boolean }>(
    apiClient.post(`${BASE}/process-templates/${id}/apply/`, { connections, reset }),
  );

export const dryRunProcessTemplate = (id: string, connections: string[], reset = false) =>
  unwrap<{ dry_run: true; reset: boolean; projects: import("./types").TemplateDiffRow[] }>(
    apiClient.post(`${BASE}/process-templates/${id}/apply/`, { connections, reset, dry_run: true }),
  );
